/**
 * Keep the app rendering where the browser refuses Web Storage.
 *
 * A document framed by a sandbox without `allow-same-origin` (and some
 * blocked-site-data modes) throws a SecurityError on the mere read of
 * `window.localStorage`, so the first component that touched it tore the whole
 * app down (Sentry 8080-AI-B3, the auth state initializer on /login). Such a
 * page gets an in-memory Storage for this visit instead: nothing persists,
 * which is all that frame could ever have had. Browsers that allow storage are
 * left untouched.
 *
 * Imported first by index.tsx, so it runs before any module reads storage.
 */

type StorageName = 'localStorage' | 'sessionStorage';

type BrowserWindow = typeof window;

type BrowserStorage = BrowserWindow['localStorage'];

const STORAGE_NAMES: StorageName[] = ['localStorage', 'sessionStorage'];

/** A Storage that lives only as long as the page. */
export class MemoryStorage implements BrowserStorage {
  private readonly items = new Map<string, string>();

  get length(): number {
    return this.items.size;
  }

  clear(): void {
    this.items.clear();
  }

  getItem(key: string): string | null {
    return this.items.has(key) ? (this.items.get(key) as string) : null;
  }

  key(index: number): string | null {
    return Array.from(this.items.keys())[index] ?? null;
  }

  removeItem(key: string): void {
    this.items.delete(key);
  }

  setItem(key: string, value: string): void {
    this.items.set(key, String(value));
  }
}

function isRefused(target: BrowserWindow, name: StorageName): boolean {
  try {
    return !target[name];
  } catch {
    return true;
  }
}

/** Give each refused storage an in-memory stand-in; returns the names replaced. */
export function installStorageFallback(target: BrowserWindow = window): StorageName[] {
  return STORAGE_NAMES.filter((name) => {
    if (!isRefused(target, name)) return false;
    try {
      Object.defineProperty(target, name, { configurable: true, value: new MemoryStorage() });

      return true;
    } catch {
      return false;
    }
  });
}

if (typeof window !== 'undefined') installStorageFallback();
